import { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowLeft, Camera, DollarSign, Pen, X, Check, AlertTriangle, Lock, ShieldCheck } from 'lucide-react';
import { useProofPhotos } from '../../hooks/useProofPhotos';
import { useRiderLocationSync } from '../../hooks/useRiderLocationSync';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface DeliveryConfirmationScreenProps {
  jobId: string;
  deliveryAddress: string;
  estimatedEarnings: string;
  isCOD?: boolean;
  codAmount?: string;
  requiresPin?: boolean;
  customerName?: string;
  riderId?: string;
  onBack: () => void;
  onConfirm: (data: {
    photos: string[];
    signature?: string;
    cashCollected?: string;
    tip?: string;
  }) => void | Promise<void>;
}

const PIN_LENGTH = 6;

export function DeliveryConfirmationScreen({
  jobId,
  deliveryAddress,
  estimatedEarnings,
  isCOD = false,
  codAmount = '0',
  requiresPin = true,
  customerName = 'Customer',
  riderId,
  onBack,
  onConfirm,
}: DeliveryConfirmationScreenProps) {
  const MAX_PHOTOS = 3;
  const { photos, error, fileInputRef, handleFileChange, openPicker, deletePhoto } =
    useProofPhotos(MAX_PHOTOS);

  useRiderLocationSync(jobId, riderId, true);
  const [signature, setSignature] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [cashCollected, setCashCollected] = useState(isCOD ? codAmount : '');
  const [selectedTip, setSelectedTip] = useState('');
  const [enteredPIN, setEnteredPIN] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinVerified, setPinVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (requiresPin === false) {
      setPinVerified(true);
      setPinError('');
    }
  }, [requiresPin]);

  const verifyPin = useCallback(
    (pin: string) => {
      if (pin.length < PIN_LENGTH) {
        setPinVerified(false);
        return;
      }
      if (!requiresPin) {
        setPinVerified(true);
        setPinError('');
        return;
      }
      // Server-side check — the customer's PIN is never on this device.
      setIsSubmitting(true);
      const functions = getFunctions(getApp(), 'us-central1');
      const verifyDeliveryPin = httpsCallable(functions, 'verifyDeliveryPin');
      verifyDeliveryPin({ jobId, orderId: jobId, pin })
        .then((res: any) => {
          if (res?.data?.ok) {
            setPinVerified(true);
            setPinError('');
          } else {
            setPinVerified(false);
            setPinError('Incorrect PIN. Ask the customer to read their 6-digit code from the GO7 app.');
          }
        })
        .catch(() => {
          setPinVerified(false);
          setPinError('Could not verify PIN. Check your connection and try again.');
        })
        .finally(() => setIsSubmitting(false));
    },
    [requiresPin, jobId]
  );

  const handlePinInput = (raw: string) => {
    const pin = raw.replace(/\D/g, '').slice(0, PIN_LENGTH);
    setEnteredPIN(pin);
    setPinError('');
    if (pin.length === PIN_LENGTH) {
      verifyPin(pin);
    } else {
      setPinVerified(false);
    }
  };

  const drawAt = (clientX: number, clientY: number, start: boolean) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (start) {
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else if (isDrawing) {
      ctx.lineTo(x, y);
      ctx.strokeStyle = '#111111';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    drawAt(e.clientX, e.clientY, true);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    drawAt(e.clientX, e.clientY, false);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);
    const touch = e.touches[0];
    if (touch) drawAt(touch.clientX, touch.clientY, true);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing) return;
    const touch = e.touches[0];
    if (touch) drawAt(touch.clientX, touch.clientY, false);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    if (canvasRef.current) setSignature(canvasRef.current.toDataURL());
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };

  const handleConfirm = async () => {
    if (!pinVerified || photos.length === 0 || isSubmitting) return;
    if (isCOD && !cashCollected) return;
    setIsSubmitting(true);
    try {
      await onConfirm({
        photos: photos.map((p) => p.uri),
        signature: signature || undefined,
        cashCollected: isCOD ? cashCollected : undefined,
        tip: selectedTip || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tipOptions = ['₦50', '₦100', '₦200'];

  return (
    <div className="absolute inset-0 flex flex-col bg-gray-50 overflow-hidden">
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="w-11 h-11 flex items-center justify-center hover:bg-gray-100 rounded-xl transition-colors touch-manipulation"
        >
          <ArrowLeft className="w-6 h-6 text-[#111111]" />
        </button>
        <div className="min-w-0">
          <h1 className="text-[18px] text-[#111111]">Complete Delivery</h1>
          <p className="text-[12px] text-[#6B6B6B] truncate">Order #{jobId}</p>
        </div>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-4"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="bg-[#00B277]/10 rounded-[16px] p-4 border border-[#00B277]/20">
          <p className="text-[12px] text-[#00B277] mb-1">DELIVERY LOCATION</p>
          <p className="text-[14px] text-[#111111] break-words">{deliveryAddress}</p>
        </div>

        <div className="bg-white rounded-[16px] p-4 border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            {pinVerified ? (
              <ShieldCheck className="w-6 h-6 text-green-600 shrink-0" />
            ) : (
              <Lock className="w-6 h-6 text-blue-600 shrink-0" />
            )}
            <div className="min-w-0">
              <p className="text-[15px] font-semibold text-[#111111]">
                {pinVerified ? 'PIN verified' : 'Enter customer PIN'}
              </p>
              <p className="text-[12px] text-[#6B6B6B]">
                Call {customerName} and ask for their 6-digit delivery PIN from the GO7 app
              </p>
            </div>
          </div>

          {!pinVerified && (
            <>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={PIN_LENGTH}
                value={enteredPIN}
                onChange={(e) => handlePinInput(e.target.value)}
                placeholder="Enter 6-digit PIN"
                className={`w-full max-w-full box-border h-14 px-4 text-center text-[24px] font-bold tracking-[0.35em] rounded-[12px] border-2 focus:outline-none focus:border-blue-500 ${
                  pinError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'
                }`}
              />
              {pinError && (
                <p className="text-[12px] text-red-600 mt-2">{pinError}</p>
              )}
            </>
          )}

          {pinVerified && (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-[12px] px-3 py-2 text-[13px]">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              Customer PIN confirmed — you can complete delivery below
            </div>
          )}
        </div>

        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            className="hidden"
            aria-hidden
          />

          <div className="flex items-center justify-between mb-2">
            <label className="text-[14px] text-[#111111]">
              Proof Photos <span className="text-[#E02020]">*</span>
            </label>
            <span className="text-[12px] text-[#6B6B6B]">
              {photos.length}/{MAX_PHOTOS}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2 w-full max-w-full">
            {photos.map((photo) => (
              <div key={photo.id} className="aspect-square relative min-w-0">
                <img
                  src={photo.uri}
                  alt="Delivery proof"
                  className="w-full h-full object-cover rounded-[12px] border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => deletePhoto(photo.id)}
                  className="absolute -top-2 -right-2 w-7 h-7 bg-[#E02020] rounded-full text-white flex items-center justify-center shadow-md"
                  aria-label="Delete photo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            {photos.length < MAX_PHOTOS && (
              <button
                type="button"
                onClick={openPicker}
                className="aspect-square min-w-0 border-2 border-dashed border-gray-300 rounded-[12px] flex flex-col items-center justify-center gap-1 hover:border-[#00B277] touch-manipulation"
              >
                <Camera className="w-6 h-6 text-[#00B277]" />
                <span className="text-[10px] text-[#6B6B6B]">Take Photo</span>
              </button>
            )}
          </div>

          {photos.length === 0 && (
            <p className="mt-2 text-[12px] text-[#E02020] flex items-center gap-1">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              At least 1 delivery photo required
            </p>
          )}
          {error && <p className="mt-2 text-[12px] text-[#E02020]">{error}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[14px] text-[#111111]">Customer Signature (Optional)</label>
            {signature && (
              <button type="button" onClick={clearSignature} className="text-[14px] text-[#E0001B]">
                Clear
              </button>
            )}
          </div>
          <div className="border-2 border-gray-300 rounded-[12px] overflow-hidden">
            <canvas
              ref={canvasRef}
              width={328}
              height={120}
              className="w-full max-w-full h-[120px] bg-white touch-none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleMouseUp}
            />
            <div className="bg-gray-50 px-3 py-2 border-t border-gray-200 flex items-center gap-2">
              <Pen className="w-4 h-4 text-[#6B6B6B]" />
              <p className="text-[12px] text-[#6B6B6B]">Customer signs above (optional)</p>
            </div>
          </div>
        </div>

        {isCOD && (
          <div className="bg-amber-50 rounded-[12px] border border-amber-200 p-4">
            <label htmlFor="cash" className="block text-[14px] mb-2 text-amber-900">
              Cash Collected (COD) <span className="text-[#E02020]">*</span>
            </label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-700" />
              <input
                id="cash"
                type="number"
                value={cashCollected}
                onChange={(e) => setCashCollected(e.target.value)}
                className="w-full max-w-full box-border h-12 pl-12 pr-4 rounded-[12px] bg-white border border-amber-300 focus:outline-none"
              />
            </div>
            <p className="text-[12px] text-amber-700 mt-2">Expected: {codAmount}</p>
          </div>
        )}

        <div className="bg-blue-50 rounded-[12px] border border-blue-100 p-4">
          <p className="text-[14px] text-blue-900 mb-3">Did the customer give a tip?</p>
          <div className="flex flex-wrap gap-2">
            {tipOptions.map((tip) => (
              <button
                key={tip}
                type="button"
                onClick={() => setSelectedTip(selectedTip === tip ? '' : tip)}
                className={`flex-1 min-w-[4rem] h-10 rounded-[12px] text-[14px] ${
                  selectedTip === tip ? 'bg-blue-600 text-white' : 'bg-white text-blue-900 border border-blue-200'
                }`}
              >
                {tip}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSelectedTip('')}
              className={`px-4 h-10 rounded-[12px] text-[14px] ${
                !selectedTip ? 'bg-blue-600 text-white' : 'bg-white text-blue-900 border border-blue-200'
              }`}
            >
              None
            </button>
          </div>
        </div>

        <div className="bg-[#00B277]/10 rounded-[12px] p-4 border border-[#00B277]/20">
          <div className="flex items-center justify-between">
            <span className="text-[14px] text-[#111111]">Your earnings</span>
            <span className="text-[18px] font-semibold text-[#00B277]">{estimatedEarnings}</span>
          </div>
        </div>

        <div className="h-2" aria-hidden />
      </div>

      <div
        className="shrink-0 p-4 border-t border-gray-200 bg-white"
        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!photos.length || !pinVerified || (isCOD && !cashCollected) || isSubmitting}
          className="w-full h-12 rounded-[12px] bg-[#00B277] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-manipulation"
        >
          <Check className="w-5 h-5" />
          {isSubmitting ? 'Completing…' : 'Complete Delivery'}
        </button>
      </div>
    </div>
  );
}
